import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LEAVE_STATUS_LABELS, type LeaveRequest, type VacationSummary } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { useSession } from '../../session';
import { VacationFacts } from './VacationFacts';
import './hr.css';

const dateDe = (d: string) => new Date(`${d}T12:00:00`).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

/**
 * Freistellungsantrag in der Anfrage (KON-121): Zeitraum, Art, Arbeitstage und der Urlaub des Mitarbeiters, dazu „Genehmigen“ und „Ablehnen“ für alle mit dem Recht,
 * Mitarbeiter zu bearbeiten. Ist die Anfrage keine Freistellung, erscheint nichts.
 */
export function InquiryLeavePanel({ inquiryId }: { inquiryId: string }) {
  const { can } = useSession();
  const client = useQueryClient();
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState('');
  const leave = useQuery({
    queryKey: ['leave-by-inquiry', inquiryId],
    queryFn: async () => (await api<{ data: (LeaveRequest & { vacation: VacationSummary | null }) | null }>('GET', `/leave-requests/by-inquiry/${inquiryId}`)).data,
  });
  const decide = useMutation({
    mutationFn: (decision: 'approved' | 'rejected') => api('POST', `/leave-requests/${leave.data!.id}/decision`, { decision, note: note.trim() || undefined }),
    onSuccess: () => {
      setRejecting(false);
      client.invalidateQueries({ queryKey: ['leave-by-inquiry', inquiryId] });
      client.invalidateQueries({ queryKey: ['inquiry', inquiryId] });
      client.invalidateQueries({ queryKey: ['inquiries'] });
    },
  });
  if (!leave.data) return null;
  const r = leave.data;
  const canDecide = r.status === 'pending' && can('hr.write');

  return (
    <section className="card" aria-label="Freistellungsantrag">
      <div className="section-head">
        <h2>Freistellungsantrag</h2>
        <span className={`leave-status leave-status--${r.status}`}>{LEAVE_STATUS_LABELS[r.status]}</span>
      </div>
      <dl className="facts">
        <dt>Mitarbeiter</dt><dd>{r.employeeName}</dd>
        <dt>Art</dt><dd>{r.kindLabel}{r.isVacation ? '' : ' (geht nicht vom Urlaub ab)'}</dd>
        <dt>Zeitraum</dt><dd>{dateDe(r.start)}{r.end !== r.start ? ` bis ${dateDe(r.end)}` : ''}, {r.workDays.toLocaleString('de-DE')} {r.workDays === 1 ? 'Arbeitstag' : 'Arbeitstage'}</dd>
        {r.decidedAt && (<><dt>Entschieden</dt><dd>{new Date(r.decidedAt).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })} von {r.decidedByName ?? 'Büro'}{r.decisionNote ? `: „${r.decisionNote}“` : ''}</dd></>)}
      </dl>
      {r.isVacation && r.vacation && <VacationFacts vacation={r.vacation} />}
      {decide.error && <div className="alert alert--error" role="alert">{decide.error instanceof ApiError ? decide.error.message : 'Das hat nicht geklappt.'}</div>}
      {canDecide && !rejecting && (
        <div className="form-actions">
          <button type="button" className="btn btn--primary" disabled={decide.isPending} onClick={() => window.confirm(`Freistellung für ${r.employeeName} genehmigen?\n\nWirkia trägt sie als ganztägigen Termin in den Kalender ein.`) && decide.mutate('approved')}>Genehmigen</button>
          <button type="button" className="btn btn--secondary" onClick={() => setRejecting(true)}>Ablehnen</button>
        </div>
      )}
      {canDecide && rejecting && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            decide.mutate('rejected');
          }}
        >
          <label className="field">
            <span>Begründung (sieht der Mitarbeiter)</span>
            <textarea className="input textarea" rows={2} value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} />
          </label>
          <div className="form-actions">
            <button type="submit" className="btn btn--danger" disabled={decide.isPending}>Ablehnen</button>
            <button type="button" className="btn btn--secondary" onClick={() => setRejecting(false)}>Abbrechen</button>
          </div>
        </form>
      )}
      {r.status === 'pending' && !can('hr.write') && <p className="muted small">Genehmigen oder ablehnen kann, wer Mitarbeiter bearbeiten darf.</p>}
    </section>
  );
}
